import React, { useEffect, useRef, useMemo } from 'react';
import * as d3 from 'd3';
import useAppStore from '../../store/appStore';

const DOMAIN_COLORS = {
  health: '#10b981',
  finance: '#f59e0b',
  career: '#3b82f6',
  relationships: '#ec4899',
  goals: '#8b5cf6',
  habits: '#14b8a6',
  events: '#64748b',
  family: '#f97316',
  meta: '#475569',
};

const NODE_RADIUS = { Goal: 10, Implication: 7, Event: 5, default: 6 };

export default function GraphView() {
  const svgRef = useRef(null);
  const atoms = useAppStore(s => s.atoms);
  const setSelectedAtom = useAppStore(s => s.setSelectedAtom);

  const { nodes, links } = useMemo(() => {
    const nodes = atoms.slice(0, 200).map(atom => ({
      id: atom.name || atom.handle,
      label: (atom.name || '').slice(0, 20),
      type: atom.type || 'Node',
      domain: atom.domain || 'meta',
      confidence: atom.truth_value?.confidence || 0.5,
      raw: atom,
    }));

    // Build links from Implication atoms and SubGoal atoms
    const links = [];
    const nodeIds = new Set(nodes.map(n => n.id));

    atoms.forEach(atom => {
      const meta = atom.metadata || {};
      if (atom.type === 'Implication' && meta.action && meta.affects) {
        const source = meta.action.replace(/\s+/g, '_');
        const target = meta.affects;
        if (nodeIds.has(target)) {
          links.push({
            source: atom.name,
            target,
            label: meta.direction || '',
            confidence: meta.confidence || 0.5,
          });
        }
      }
      if (atom.type === 'SubGoal' && meta.parent) {
        if (nodeIds.has(meta.parent)) {
          links.push({ source: meta.parent, target: atom.name, label: 'subgoal' });
        }
      }
    });

    return { nodes, links };
  }, [atoms]);

  useEffect(() => {
    if (!svgRef.current || nodes.length === 0) return;

    const el = svgRef.current;
    const width = el.parentElement?.clientWidth || 800;
    const height = el.parentElement?.clientHeight || 600;

    d3.select(el).selectAll('*').remove();

    const svg = d3.select(el)
      .attr('width', width)
      .attr('height', height);

    // Zoom + pan
    const g = svg.append('g');
    svg.call(
      d3.zoom()
        .scaleExtent([0.1, 4])
        .on('zoom', (event) => g.attr('transform', event.transform))
    );

    // Simulation
    const simulation = d3.forceSimulation(nodes)
      .force('link', d3.forceLink(links).id(d => d.id).distance(80).strength(0.3))
      .force('charge', d3.forceManyBody().strength(-150))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide(20));

    // Links
    const link = g.append('g')
      .selectAll('line')
      .data(links)
      .join('line')
      .attr('stroke', d => d.label === 'positive' ? '#10b981' : d.label === 'negative' ? '#ef4444' : '#475569')
      .attr('stroke-opacity', d => 0.3 + (d.confidence || 0.5) * 0.5)
      .attr('stroke-width', 1.5);

    // Nodes
    const node = g.append('g')
      .selectAll('circle')
      .data(nodes)
      .join('circle')
      .attr('r', d => NODE_RADIUS[d.type] || NODE_RADIUS.default)
      .attr('fill', d => DOMAIN_COLORS[d.domain] || '#64748b')
      .attr('fill-opacity', d => 0.4 + d.confidence * 0.6)
      .attr('stroke', d => DOMAIN_COLORS[d.domain] || '#64748b')
      .attr('stroke-width', 2)
      .attr('cursor', 'pointer')
      .call(
        d3.drag()
          .on('start', (event, d) => {
            if (!event.active) simulation.alphaTarget(0.3).restart();
            d.fx = d.x; d.fy = d.y;
          })
          .on('drag', (event, d) => { d.fx = event.x; d.fy = event.y; })
          .on('end', (event, d) => {
            if (!event.active) simulation.alphaTarget(0);
            d.fx = null; d.fy = null;
          })
      )
      .on('click', (event, d) => {
        event.stopPropagation();
        setSelectedAtom(d.raw);
      });

    // Labels
    const label = g.append('g')
      .selectAll('text')
      .data(nodes)
      .join('text')
      .text(d => d.label)
      .attr('font-size', '9px')
      .attr('fill', '#94a3b8')
      .attr('dx', 8)
      .attr('dy', 3)
      .attr('pointer-events', 'none');

    // Tooltip
    node.append('title').text(d =>
      `${d.type}: ${d.label}\nDomain: ${d.domain}\nConfidence: ${(d.confidence * 100).toFixed(0)}%`
    );

    simulation.on('tick', () => {
      link
        .attr('x1', d => d.source.x).attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x).attr('y2', d => d.target.y);
      node.attr('cx', d => d.x).attr('cy', d => d.y);
      label.attr('x', d => d.x).attr('y', d => d.y);
    });

    return () => simulation.stop();
  }, [nodes, links, setSelectedAtom]);

  if (nodes.length === 0) {
    return (
      <div className="graph-empty">
        <p>No atoms in knowledge graph yet.</p>
        <p>Start a conversation to build your personal DAS.</p>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <svg ref={svgRef} style={{ width: '100%', height: '100%' }} />
      <GraphLegend />
    </div>
  );
}

function GraphLegend() {
  return (
    <div style={{
      position: 'absolute', bottom: 12, left: 12,
      background: 'rgba(15,23,42,0.85)', borderRadius: 8, padding: '8px 12px',
      border: '1px solid #334155',
    }}>
      <div style={{ fontSize: 10, color: '#64748b', marginBottom: 4 }}>DOMAINS</div>
      {Object.entries(DOMAIN_COLORS).filter(([d]) => d !== 'meta').map(([domain, color]) => (
        <div key={domain} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
          <span style={{ fontSize: 10, color: '#94a3b8', textTransform: 'capitalize' }}>{domain}</span>
        </div>
      ))}
    </div>
  );
}
